import { TextField, Tabs, Box, IconButton } from '@radix-ui/themes'
import TradingNavButton from '@/components/TradingNavButton'
import { inputBottomBorder } from '@/styles/textFieldStyles'
import { useState, useMemo, useEffect } from 'react'
import TradingChart from '@/components/TradingChart'
import { useBalance } from '@/hooks/useBalance'
import Skeleton from '@/components/common/Skeleton'
import FutureFunctionDialog from '@/components/dialogs/FutureFunctionDialog'
import { useTranslation } from 'react-i18next'

const Trading = () => {
  const { t } = useTranslation()
  const { tokens, loading } = useBalance()
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    null
  )
  const [activeTab, setActiveTab] = useState<'all' | 'gainers'>('all')
  const [selectedCoin, setSelectedCoin] = useState<string>('')
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set())

  // Registrar cada pestaña que se visita para renderizarla (lazy load)
  useEffect(() => {
    if (selectedCoin) {
      setVisitedTabs((prev) => new Set(prev).add(selectedCoin))
    }
  }, [selectedCoin])

  // Usar dinámicamente las monedas de la billetera y aplicar filtro de Top Gainers
  const tradingTokens = useMemo(() => {
    let currentTokens = tokens.filter((t) => t.symbol && t.symbol.trim() !== '')

    // Eliminar duplicados si los hubiera basándose en el símbolo
    const uniqueTokens = []
    const seen = new Set()
    for (const t of currentTokens) {
      if (!seen.has(t.symbol)) {
        seen.add(t.symbol)
        uniqueTokens.push(t)
      }
    }

    if (activeTab === 'gainers') {
      // Ordenar por cambio de precio en 24h (descendente) y tomar los top 5
      return uniqueTokens
        .sort((a, b) => (b.priceChange24h || 0) - (a.priceChange24h || 0))
        .slice(0, 5)
    }

    return uniqueTokens
  }, [tokens, activeTab])

  // Asegurar que siempre haya una moneda seleccionada si la lista cambia
  useEffect(() => {
    if (tradingTokens.length > 0) {
      if (
        !selectedCoin ||
        !tradingTokens.find((t) => t.symbol === selectedCoin)
      ) {
        setSelectedCoin(tradingTokens[0].symbol)
      }
    }
  }, [tradingTokens, selectedCoin])

  const handlePercentageClick = (percentage: number) => {
    setSelectedPercentage(percentage)
  }

  return (
    <div className="flex flex-col w-full gap-8 relative overflow-hidden rounded-3xl h-[calc(100vh-150px)] p-2">
      {/*NAVIGATION */}
      <h1 className="text-4xl font-extrabold text-text-main w-full">
        {t('trading.title')}
      </h1>
      <div className="w-full overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-2 min-w-max">
          <TradingNavButton
            name={t('trading.all_assets')}
            currentTab={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
          />

          <TradingNavButton
            name={t('trading.top_gainers')}
            currentTab={activeTab === 'gainers'}
            onClick={() => setActiveTab('gainers')}
          />
        </div>
      </div>

      <Tabs.Root
        value={selectedCoin}
        onValueChange={setSelectedCoin}
        style={{ width: '100%' }}
      >
        <Tabs.List
          color="amber"
          style={{
            justifyContent: 'space-around',
            display: 'flex',
            flexWrap: 'wrap'
          }}
        >
          {tradingTokens.map((token) => (
            <Tabs.Trigger
              key={token.symbol}
              value={token.symbol}
              style={{ flexGrow: 1, minWidth: '60px' }}
            >
              {token.symbol}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Box
          pt="3"
          style={{ position: 'relative', width: '100%', minHeight: '370px' }}
        >
          {loading ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-75 w-full rounded-xl" />
            </div>
          ) : (
            tradingTokens.map((token) => {
              if (!visitedTabs.has(token.symbol)) return null
              const isActive = selectedCoin === token.symbol
              return (
                <div
                  key={token.symbol}
                  style={{
                    position: isActive ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: isActive ? 'auto' : '0px',
                    visibility: isActive ? 'visible' : 'hidden',
                    opacity: isActive ? 1 : 0,
                    overflow: 'hidden',
                    pointerEvents: isActive ? 'auto' : 'none',
                    zIndex: isActive ? 1 : -1
                  }}
                >
                  <TradingChart symbol={token.symbol} />
                </div>
              )
            })
          )}
        </Box>
      </Tabs.Root>

      {/* BUY SELL BUTTONS */}

      <div className="flex gap-2 h-9.5 -mt-8">
        <button
          className="btn-gradient-fill rounded-2xl w-1/2 lg:h-12 font-bold"
          onClick={() => {}}
        >
          {t('trading.buy')}
        </button>
        <button
          className="btn-gradient-outline rounded-2xl w-1/2 lg:h-12 font-bold"
          onClick={() => {}}
        >
          {t('trading.sell')}
        </button>
      </div>
      <FutureFunctionDialog
        open={true}
        preventClose
        isPageScoped
      />

      <div className="flex flex-col gap-2 bg-card p-6 rounded-3xl border border-border-base shadow-md">
        <div className="flex flex-col gap-0.5 w-full">
          <label className="text-sm text-text-secondary font-medium">
            {t('trading.at_price')}
          </label>
          <TextField.Root
            size="3"
            type="number"
            variant="surface"
            placeholder="0"
            style={inputBottomBorder}
          />
        </div>

        <div className="flex flex-col gap-0.5 w-full mt-4">
          <label className="text-sm text-text-secondary font-medium">
            {t('trading.amount')}
          </label>
          <TextField.Root
            size="3"
            type="number"
            variant="surface"
            placeholder="0"
            style={inputBottomBorder}
          >
            <TextField.Slot />
            <TextField.Slot className="text-text-secondary">
              <div className="flex gap-6">
                {[25, 50, 100].map((pct) => (
                  <IconButton
                    key={pct}
                    size="1"
                    variant="ghost"
                    radius="large"
                    color="gray"
                    onClick={() => handlePercentageClick(pct)}
                  >
                    <span
                      className={`text-xs ${
                        selectedPercentage === pct
                          ? 'text-text-main font-semibold '
                          : 'text-text-disabled'
                      }`}
                    >
                      {pct}%
                    </span>
                  </IconButton>
                ))}
              </div>
            </TextField.Slot>
          </TextField.Root>
        </div>

        <button
          onClick={() => {}}
          className="lg:w-1/5 place-self-end btn-gradient-fill w-full py-4 rounded-2xl font-bold mt-8 shadow-primary-glow"
        >
          {t('trading.execute_order')}
        </button>
      </div>
    </div>
  )
}

export default Trading
